// Studio edits → typed CreativeOperation[] (the same operation language the
// AI uses, ADR-0018 §2.1-6). Nothing here mutates a document or decides
// validity: the editor applies the operations with the Creative Engine's
// applyAndValidate, which accepts or rejects them.
//
// Format variants (CREATIVE-MODEL §4.2): an element's BASE values live in its
// home variant; an edit in another variant writes THAT variant's override, so
// variants stay independent. Elements created in a variant exist only there
// (`onlyIn`), and a variant created from another gets explicit copies of the
// resolved frames — never a proportional scaling.

import type {
  Action,
  AssetRef,
  AuthoringState,
  BoxStyle,
  CreativeDocument,
  CreativeOperation,
  Element,
  Frame,
  LayoutOverride,
  Rule,
  Scene,
  TextContent,
  Typography,
} from "@interactads/creative-model";
import { layoutIdForFormat, resolveVariant } from "@interactads/creative-model";
import {
  MAIN_LINK_ID,
  editingScene,
  findElement,
  findLayout,
  homeLayout,
  layerOrder,
  layoutsOf,
  linkOfElement,
  masterLayout,
  nextId,
  overrideOf,
  ownLinkId,
  presentIn,
  sceneOfElement,
  tapRuleId,
  variantViews,
} from "./model";
import { pruneGame } from "./game-prune";
import { pruneRule, type Removed } from "./references";

type Ops = CreativeOperation[];
type Json = Record<string, unknown>;

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** Presentation properties a variant may override (LayoutOverride). */
export interface PresentationPatch {
  readonly frame?: Frame;
  readonly typography?: Partial<Typography>;
  readonly style?: Partial<BoxStyle>;
  /** Text of a text element / label of a button. */
  readonly content?: TextContent;
  readonly visible?: boolean;
  readonly fit?: "cover" | "contain" | "fill" | "none";
}

/** Whether edits of `elementId` in `layoutId` write the element's base values (home variant). */
export function editsBase(doc: CreativeDocument, layoutId: string, elementId: string): boolean {
  const el = findElement(doc, elementId);
  return el !== undefined && homeLayout(doc, el) === layoutId;
}

/** Writes presentation values of one element in one variant (base in its home variant, override elsewhere). */
export function patchPresentation(doc: CreativeDocument, layoutId: string, id: string, patch: PresentationPatch): Ops {
  const el = findElement(doc, id);
  if (!el) return [];
  return [...ownPresentation(doc, layoutId, el, patch), ...carouselGeometry(doc, layoutId, el, patch.frame)];
}

function ownPresentation(doc: CreativeDocument, layoutId: string, el: Element, patch: PresentationPatch): Ops {
  const id = el.id;
  if (editsBase(doc, layoutId, id)) {
    const set: Json = {};
    if (patch.frame) set.frame = patch.frame;
    if (patch.visible !== undefined) set.visible = patch.visible;
    if (patch.fit !== undefined) set.fit = patch.fit;
    if (patch.typography && "typography" in el) set.typography = { ...el.typography, ...patch.typography };
    if (patch.style) set.style = { ...(el.style ?? {}), ...patch.style };
    if (patch.content) set[el.type === "button" ? "label" : "content"] = patch.content;
    return [{ op: "updateElement", id, set }];
  }
  const current: LayoutOverride = overrideOf(doc, layoutId, id) ?? {};
  const next: Json = { ...clone(current) };
  if (patch.frame) next.frame = patch.frame;
  if (patch.visible !== undefined) next.visible = patch.visible;
  if (patch.fit !== undefined) next.fit = patch.fit;
  if (patch.typography) next.typography = { ...(current.typography ?? {}), ...patch.typography };
  if (patch.style) next.style = { ...(current.style ?? {}), ...patch.style };
  if (patch.content) next.content = patch.content;
  return [{ op: "setLayoutOverride", layout: layoutId, element: id, override: next }];
}

/** Caption band height of a carousel slide of height `h` (Studio convention for structured slides). */
export const captionHeight = (h: number) => Math.max(20, Math.min(48, Math.round(h * 0.18)));

/**
 * A carousel's slides fill its viewport. When its size changes in a variant,
 * the slides and their FULL-BLEED layers (background, picture) and bottom
 * caption band are re-framed explicitly in that variant — exact new frames,
 * never a proportional scaling. Layers placed any other way are left as they are.
 */
function carouselGeometry(doc: CreativeDocument, layoutId: string, el: Element, frame: Frame | undefined): Ops {
  if (!frame || el.type !== "component" || el.component !== "ia.carousel@1") return [];
  const resolved = resolveVariant(doc, layoutId);
  const before = resolved?.elements[el.id]?.frame as unknown as Frame | undefined;
  if (!resolved || !before || (before.width === frame.width && before.height === frame.height)) return [];
  const { width: w, height: h } = frame;
  const ops: Ops = [];
  for (const slideId of el.slots?.slides ?? []) {
    const slide = findElement(doc, slideId);
    if (!slide) continue;
    ops.push(...ownPresentation(doc, layoutId, slide, { frame: { x: 0, y: 0, width: w, height: h } }));
    if (slide.type !== "group") continue;
    for (const childId of slide.children) {
      const child = findElement(doc, childId);
      const f = resolved.elements[childId]?.frame as unknown as Frame | undefined;
      if (!child || !f) continue;
      const fullBleed = f.x === 0 && f.y === 0 && f.width === before.width && f.height === before.height;
      const caption =
        child.type === "text" && f.x === 0 && f.width === before.width && f.y + f.height === before.height;
      if (fullBleed) ops.push(...ownPresentation(doc, layoutId, child, { frame: { x: 0, y: 0, width: w, height: h } }));
      else if (caption) {
        const ch = captionHeight(h);
        ops.push(...ownPresentation(doc, layoutId, child, { frame: { x: 0, y: h - ch, width: w, height: ch } }));
      }
    }
  }
  return ops;
}

/** Removes base style keys (home variant) — in other variants a style can only be overridden, not removed. */
export function unsetBaseStyle(doc: CreativeDocument, id: string, keys: readonly (keyof BoxStyle)[]): Ops {
  const el = findElement(doc, id);
  if (!el?.style) return [];
  const style = Object.fromEntries(Object.entries(el.style).filter(([k]) => !keys.includes(k as keyof BoxStyle)));
  return Object.keys(style).length === 0
    ? [{ op: "updateElement", id, set: {}, unset: ["style"] }]
    : [{ op: "updateElement", id, set: { style } }];
}

/** Properties shared by every variant (not overridable per format): opacity, name, asset, alt, shape, role… */
export function updateShared(id: string, set: Json, unset?: readonly string[]): Ops {
  return [{ op: "updateElement", id, set, ...(unset && unset.length > 0 ? { unset: [...unset] } : {}) }];
}

export function rename(doc: CreativeDocument, id: string, name: string): Ops {
  const trimmed = name.trim();
  if (!findElement(doc, id)) return [];
  return trimmed === "" ? updateShared(id, {}, ["name"]) : updateShared(id, { name: trimmed });
}

/** Scene composition update that keeps every other variant's own order list intact. */
function sceneSet(scene: Scene, set: Json): CreativeOperation {
  return { op: "updateScene", id: scene.id, set };
}

/** Adds a new element at the front of the current variant of a scene (it exists only in that variant). */
export function addElement(doc: CreativeDocument, layoutId: string, element: Element, sceneId?: string): Ops {
  const scene = editingScene(doc, sceneId);
  const ops: Ops = [{ op: "addElement", element, parent: { in: "scene", id: scene.id } }];
  const own = scene.childrenByLayout?.[layoutId];
  if (own)
    ops.push(sceneSet(scene, { childrenByLayout: { ...scene.childrenByLayout, [layoutId]: [...own, element.id] } }));
  return ops;
}

/**
 * What removing elements does to the interactions that reference them (an
 * explicit consequence of the user's delete, reported back as a count): rules
 * triggered by them go, actions aimed at them are dropped (a rule left with no
 * action goes too). Nothing else is touched.
 */
export function pruneRulesFor(doc: CreativeDocument, removed: Removed): Ops {
  const ops: Ops = [];
  for (const rule of doc.rules ?? []) {
    const next = pruneRule(rule, removed);
    if (next === rule) continue;
    ops.push(next === null ? { op: "removeRule", id: rule.id } : { op: "replaceRule", rule: next });
  }
  return ops;
}

/** Interactions affected by removing these elements (for confirmation copy). */
export const affectedRules = (doc: CreativeDocument, removed: Removed) =>
  (doc.rules ?? []).filter((r) => pruneRule(r, removed) !== r);

/** Element ids of the subtree rooted at `id` (groups and component slots), the root first. */
export function subtreeIds(doc: CreativeDocument, id: string): string[] {
  const out: string[] = [];
  const visit = (x: string) => {
    if (out.includes(x)) return;
    const el = findElement(doc, x);
    if (!el) return;
    out.push(x);
    if (el.type === "group") el.children.forEach(visit);
    if (el.type === "component")
      Object.values(el.slots ?? {}).forEach((ids) => {
        ids.forEach(visit);
      });
  };
  visit(id);
  return out;
}

/** Asset keys an element references directly (content, poster, captions, state swaps). */
export function assetKeysOf(el: Element): string[] {
  const keys: string[] = [];
  if (el.type === "image" || el.type === "video" || el.type === "audio") keys.push(el.asset);
  if (el.type === "video") {
    if (el.poster) keys.push(el.poster);
    if (el.captions) keys.push(el.captions);
  }
  for (const patch of Object.values(el.states ?? {})) if (patch.asset) keys.push(patch.asset);
  return keys;
}

/** Whether a pinned asset is still referenced by anything outside `ignoring` elements. */
export function assetInUse(doc: CreativeDocument, key: string, ignoring: readonly string[] = []): boolean {
  if (doc.elements.some((e) => !ignoring.includes(e.id) && assetKeysOf(e).includes(key))) return true;
  if (Object.values(doc.fallback?.perLayout ?? {}).some((f) => f.asset === key)) return true;
  if (doc.scenes.some((sc) => sc.background?.kind === "image" && sc.background.asset === key)) return true;
  if ((doc.fonts ?? []).some((f) => f.asset === key)) return true;
  return (doc.survey?.questions ?? []).some((q) => q.kind !== "scale" && q.options.some((o) => o.image === key));
}

/**
 * Everything removing these elements implies, as explicit operations: rules
 * triggered by/aimed at them, own CTA links, assets nothing else uses, and
 * form fields no other element still shows (a form left without fields goes,
 * with the interactions that submit it).
 */
function cleanupAfterRemoval(doc: CreativeDocument, ids: readonly string[]): Ops {
  const removed = new Set(ids);
  const forms: string[] = [];
  const formOps: Ops = [];
  for (const form of doc.forms ?? []) {
    const shownBy = (field: string) =>
      doc.elements.some((e) => e.type === "field" && e.form === form.id && e.field === field && !removed.has(e.id));
    const lost = doc.elements.some((e) => e.type === "field" && e.form === form.id && removed.has(e.id));
    if (!lost) continue;
    const fields = form.fields.filter((f) => shownBy(f.id));
    if (fields.length === 0) {
      forms.push(form.id);
      formOps.push({ op: "removeForm", id: form.id });
    } else if (fields.length !== form.fields.length) formOps.push({ op: "setForm", form: { ...clone(form), fields } });
  }
  const ops: Ops = pruneRulesFor(doc, { elements: ids, forms });
  // Layers that played a role in the game leave it (a card takes its pair) — 7.6F.
  ops.push(...pruneGame(doc, { elements: ids }).ops);
  for (const id of ids)
    if (doc.links.some((l) => l.id === ownLinkId(id))) ops.push({ op: "removeLink", id: ownLinkId(id) });
  ops.push(...formOps);
  const keys = new Set(
    ids.flatMap((id) => {
      const el = findElement(doc, id);
      return el ? assetKeysOf(el) : [];
    })
  );
  for (const key of keys) if (!assetInUse(doc, key, ids)) ops.push({ op: "unpinAsset", key });
  return ops;
}

/** Removes an element from every variant (with its CTA rule/link and unused image pin). */
export function removeEverywhere(doc: CreativeDocument, id: string): Ops {
  if (!findElement(doc, id)) return [];
  return [{ op: "removeElement", id }, ...cleanupAfterRemoval(doc, subtreeIds(doc, id))];
}

/**
 * Delete in the current variant: if the element also exists in other
 * variants it disappears only from this one; otherwise it is removed.
 */
export function removeFromVariant(doc: CreativeDocument, layoutId: string, id: string): Ops {
  const el = findElement(doc, id);
  if (!el || !presentIn(el, layoutId)) return [];
  const present = layoutsOf(doc, el);
  if (present.length <= 1) return removeEverywhere(doc, id);
  const ops: Ops = [{ op: "updateElement", id, set: { onlyIn: present.filter((l) => l !== layoutId) } }];
  if (overrideOf(doc, layoutId, id)) ops.push({ op: "setLayoutOverride", layout: layoutId, element: id });
  const scene = sceneOfElement(doc, id) ?? editingScene(doc);
  const own = scene.childrenByLayout?.[layoutId];
  if (own?.includes(id))
    ops.push(
      sceneSet(scene, { childrenByLayout: { ...scene.childrenByLayout, [layoutId]: own.filter((x) => x !== id) } })
    );
  return ops;
}

/**
 * Whether a layer can be duplicated/pasted. Layers BOUND to a document module
 * (a form field, the survey question or survey progress) present ONE instrument:
 * a copy would show the same question twice, so it is refused with a reason.
 */
export function canDuplicate(el: Element): boolean {
  if (el.type === "field") return false;
  if (el.type === "component") return el.component === "ia.carousel@1";
  return true;
}

/** A standalone copy of the element AS RESOLVED in this variant, placed right above it, only in this variant. */
export function duplicate(
  doc: CreativeDocument,
  layoutId: string,
  id: string,
  options: { readonly offset?: number; readonly newId?: string; readonly into?: string; readonly scene?: string } = {}
): { ops: Ops; id: string } | null {
  const el = findElement(doc, id);
  const source = sceneOfElement(doc, id);
  const view = variantViews(doc, layoutId, source?.id).find((v) => v.element.id === id);
  const into = options.into ?? layoutId;
  if (!el || !view || !findLayout(doc, into) || !canDuplicate(el)) return null;
  const taken = doc.elements.map((e) => e.id);
  const newId = options.newId ?? nextId(taken, el.type === "component" ? "carousel" : el.type);
  taken.push(newId);
  const offset = options.offset ?? 10;
  const copy: Json = { ...resolvedCopy(el, view.presentation), id: newId, onlyIn: [into] };
  const frame = clone(view.frame);
  copy.frame = { ...frame, x: frame.x + offset, y: frame.y + offset };
  if (el.name) copy.name = `${el.name} copia`.slice(0, 120);
  // Nested layers (carousel slides) get fresh ids; they inherit the copy's variant and keep
  // the frames/styles they have in the source variant. Copies start with empty child lists
  // and every nested layer is added, parents first, into its copied parent.
  const ids = new Map<string, string>([[id, newId]]);
  const subtree = subtreeIds(doc, id);
  for (const childId of subtree.slice(1)) {
    const fresh = nextId(taken, childId.replace(/[0-9]+$/, "") || "el");
    taken.push(fresh);
    ids.set(childId, fresh);
  }
  const emptied = (c: Json) => {
    if (Array.isArray(c.children)) c.children = [];
    if (c.slots) c.slots = Object.fromEntries(Object.keys(c.slots as Record<string, string[]>).map((k) => [k, []]));
    return c;
  };
  emptied(copy);
  const scene = editingScene(doc, options.scene ?? source?.id);
  const index = scene.children.indexOf(id);
  const ops: Ops = [
    {
      op: "addElement",
      element: copy,
      parent: { in: "scene", id: scene.id },
      ...(index >= 0 ? { index: index + 1 } : {}),
    } as CreativeOperation,
  ];
  const resolved = subtree.length > 1 ? resolveVariant(doc, layoutId) : undefined;
  for (const childId of subtree.slice(1)) {
    const child = findElement(doc, childId);
    const parent = doc.elements.find(
      (e) =>
        (e.type === "group" && e.children.includes(childId)) ||
        (e.type === "component" && Object.values(e.slots ?? {}).some((l) => l.includes(childId)))
    );
    const parentCopy = parent ? ids.get(parent.id) : undefined;
    if (!child || !parent || !parentCopy) continue;
    const c = emptied({ ...resolvedCopy(child, resolved?.elements[childId] ?? {}), id: ids.get(childId) });
    delete c.onlyIn;
    const slot =
      parent.type === "component"
        ? Object.entries(parent.slots ?? {}).find(([, l]) => l.includes(childId))?.[0]
        : undefined;
    ops.push({
      op: "addElement",
      element: c,
      parent: slot ? { in: "slot", id: parentCopy, slot } : { in: "group", id: parentCopy },
    } as unknown as CreativeOperation);
  }
  const own = scene.childrenByLayout?.[into];
  if (own) {
    const at = own.indexOf(id);
    const list = [...own];
    list.splice(at >= 0 ? at + 1 : list.length, 0, newId);
    ops.push(sceneSet(scene, { childrenByLayout: { ...scene.childrenByLayout, [into]: list } }));
  }
  ops.push(...copyRules(doc, ids));
  return { ops, id: newId };
}

/** A copy of an element carrying the values it has in one variant (presentation resolved). */
function resolvedCopy(el: Element, p: Json): Json {
  const copy: Json = { ...clone(el) };
  if (p.visible === false) copy.visible = false;
  else delete copy.visible;
  if (p.frame) copy.frame = clone(p.frame);
  if (p.typography) copy.typography = clone(p.typography);
  if (p.style) copy.style = clone(p.style);
  if (p.text) copy[el.type === "button" ? "label" : "content"] = clone(p.text);
  if (p.fit) copy.fit = p.fit;
  if (p.animations) copy.animations = clone(p.animations);
  if (p.states) copy.states = clone(p.states);
  if (p.constraints) copy.constraints = clone(p.constraints);
  return copy;
}

/**
 * Moves a layer to `toIndex` of the variant's back-to-front order. Only that
 * variant's z-order changes: variants that still share the base order get
 * their current order recorded first.
 */
export function reorder(doc: CreativeDocument, layoutId: string, id: string, toIndex: number): Ops {
  const scene = sceneOfElement(doc, id) ?? editingScene(doc);
  const order = layerOrder(doc, layoutId, scene.id);
  const from = order.indexOf(id);
  if (from < 0) return [];
  const target = Math.max(0, Math.min(order.length - 1, toIndex));
  if (target === from) return [];
  const next = [...order];
  next.splice(from, 1);
  next.splice(target, 0, id);
  const byLayout: Record<string, string[]> = clone(scene.childrenByLayout ?? {}) as Record<string, string[]>;
  const set: Json = {};
  if (layoutId === masterLayout(doc).id && !scene.childrenByLayout?.[layoutId]) {
    // Variants without an own order list follow the base order: record theirs, then reorder the base.
    for (const l of doc.layouts)
      if (l.id !== layoutId && !Object.hasOwn(byLayout, l.id)) byLayout[l.id] = layerOrder(doc, l.id, scene.id);
    set.children = [...next, ...scene.children.filter((c) => !next.includes(c))];
  } else {
    byLayout[layoutId] = next;
  }
  if (Object.keys(byLayout).length > 0) set.childrenByLayout = byLayout;
  return [sceneSet(scene, set)];
}

// ---------------------------------------------------------------- format variants

export interface NewVariant {
  readonly key: string;
  readonly width: number;
  readonly height: number;
}

/**
 * A new format variant. Empty, or started FROM another variant: the new one
 * gets explicit copies of the source's resolved frames, overrides and layer
 * order, so editing either never changes the other.
 */
export function addVariant(doc: CreativeDocument, variant: NewVariant, from?: string): { ops: Ops; layoutId: string } {
  const taken = doc.layouts.map((l) => l.id);
  let layoutId = layoutIdForFormat(variant.key);
  if (taken.includes(layoutId)) layoutId = nextId(taken, `${layoutId}_`);
  const ops: Ops = [
    {
      op: "addLayout",
      layout: {
        id: layoutId,
        formatKeys: [variant.key],
        sizing: { mode: "fixed", width: variant.width, height: variant.height },
      },
    },
  ];
  const source = from ? findLayout(doc, from) : undefined;
  const resolvedViews = source ? doc.scenes.flatMap((sc) => variantViews(doc, source.id, sc.id)) : [];
  for (const el of doc.elements) {
    const inSource = source ? presentIn(el, source.id) : false;
    if (inSource && source) {
      if (el.onlyIn) ops.push({ op: "updateElement", id: el.id, set: { onlyIn: [...el.onlyIn, layoutId] } });
      const resolved = resolvedViews.find((v) => v.element.id === el.id);
      const override: Json = clone(overrideOf(doc, source.id, el.id) ?? {});
      if (resolved) override.frame = clone(resolved.frame);
      // Animations are per format: the copy gets its own explicit tracks (never shared with the source).
      const tracks = (resolved?.presentation as Json | undefined)?.animations;
      if (Array.isArray(tracks) && tracks.length > 0 && override.animations === undefined)
        override.animations = clone(resolvedTracks(doc, source.id, el.id));
      ops.push({ op: "setLayoutOverride", layout: layoutId, element: el.id, override });
    } else if (!el.onlyIn) {
      // Elements of every variant would otherwise appear in the new (empty) one.
      ops.push({ op: "updateElement", id: el.id, set: { onlyIn: layoutsOf(doc, el) } });
    }
  }
  for (const scene of doc.scenes) {
    const order = source ? layerOrder(doc, source.id, scene.id) : [];
    ops.push(sceneSet(scene, { childrenByLayout: { ...(scene.childrenByLayout ?? {}), [layoutId]: order } }));
  }
  return { ops, layoutId };
}

export function removeVariant(doc: CreativeDocument, layoutId: string): Ops {
  return doc.layouts.length > 1 && findLayout(doc, layoutId) ? [{ op: "removeLayout", id: layoutId }] : [];
}

export function variantProvenance(authoring: AuthoringState, layoutId: string, from?: string): AuthoringState {
  return {
    ...authoring,
    variants: {
      ...(authoring.variants ?? {}),
      [layoutId]: { ...(from ? { adaptedFrom: from } : {}), method: "manual", refinedManually: false },
    },
  };
}

// ---------------------------------------------------------------- links, CTA, fallback

const tapRule = (elementId: string, linkId: string): CreativeOperation => ({
  op: "addRule",
  rule: {
    id: tapRuleId(elementId),
    on: { type: "pointer.tap", target: elementId },
    do: [{ type: "link.open", link: linkId }],
  },
});

/** The ad's main destination (used by CTAs by default and by the fallback). Empty removes it. */
export function setMainLink(doc: CreativeDocument, url: string): Ops {
  const trimmed = url.trim();
  if (trimmed === "")
    return doc.links.some((l) => l.id === MAIN_LINK_ID) ? [{ op: "removeLink", id: MAIN_LINK_ID }] : [];
  return [{ op: "setLink", link: { id: MAIN_LINK_ID, kind: "landing", url: trimmed, label: "Destino principal" } }];
}

export type Destination =
  { readonly mode: "main" } | { readonly mode: "custom"; readonly url: string } | { readonly mode: "none" };

/**
 * Click-through of a button/hotspot: a declared link + the element's CTA rule
 * (`tap_<id>`: pointer.tap → link.open; never an inline URL). Other actions
 * the user added to that rule (go to a scene, show an element…) are kept.
 */
export function setDestination(doc: CreativeDocument, elementId: string, destination: Destination): Ops {
  const ops: Ops = [];
  const rule = doc.rules?.find((r) => r.id === tapRuleId(elementId));
  const hasOwn = doc.links.some((l) => l.id === ownLinkId(elementId));
  const others: Action[] = rule ? rule.do.filter((a) => a.type !== "link.open").map((a) => clone(a)) : [];
  const linkId =
    destination.mode === "custom" ? ownLinkId(elementId) : destination.mode === "main" ? MAIN_LINK_ID : null;
  if (destination.mode === "custom")
    ops.push({ op: "setLink", link: { id: ownLinkId(elementId), kind: "landing", url: destination.url.trim() } });
  const actions: Action[] = linkId ? [{ type: "link.open", link: linkId }, ...others] : others;
  if (rule) {
    ops.push(
      actions.length === 0
        ? { op: "removeRule", id: rule.id }
        : { op: "replaceRule", rule: { ...clone(rule), do: actions } }
    );
  } else if (linkId) ops.push(tapRule(elementId, linkId));
  if (destination.mode !== "custom" && hasOwn) ops.push({ op: "removeLink", id: ownLinkId(elementId) });
  return ops;
}

export function destinationOf(doc: CreativeDocument, elementId: string): Destination {
  const link = linkOfElement(doc, elementId);
  if (!link) return { mode: "none" };
  if (link === MAIN_LINK_ID) return { mode: "main" };
  return { mode: "custom", url: doc.links.find((l) => l.id === link)?.url ?? "" };
}

/** Pins an asset revision (reusing the key when that exact revision is already pinned). */
export function pin(doc: CreativeDocument, ref: Omit<AssetRef, "key">, prefix: string): { ops: Ops; key: string } {
  const existing = doc.assets.find((a) => a.revisionId === ref.revisionId && a.role === ref.role);
  if (existing) return { ops: [], key: existing.key };
  const key = nextId(
    doc.assets.map((a) => a.key),
    prefix
  );
  return { ops: [{ op: "pinAsset", asset: { ...ref, key } }], key };
}

/** Points an image element to another pinned asset; unpins the previous one when nothing else uses it. */
export function replaceImage(doc: CreativeDocument, elementId: string, key: string): Ops {
  const el = findElement(doc, elementId);
  if (el?.type !== "image" || el.asset === key) return [];
  const ops: Ops = updateShared(elementId, { asset: key });
  if (!assetInUse(doc, el.asset, [elementId])) ops.push({ op: "unpinAsset", key: el.asset });
  return ops;
}

/** Sets the uploaded static fallback image of one variant (alt + click destination shared by all variants). */
export function setFallbackImage(doc: CreativeDocument, layoutId: string, assetKey: string, alt: string): Ops {
  const previous = doc.fallback?.perLayout[layoutId]?.asset;
  const fallback = {
    perLayout: { ...(doc.fallback?.perLayout ?? {}), [layoutId]: { source: "uploaded", asset: assetKey } },
    linkId: doc.fallback?.linkId ?? MAIN_LINK_ID,
    alt: doc.fallback?.alt ?? alt,
  };
  const ops: Ops = [{ op: "setFallback", fallback } as CreativeOperation];
  const others = Object.entries(fallback.perLayout).filter(([l]) => l !== layoutId);
  if (
    previous &&
    previous !== assetKey &&
    !doc.elements.some((e) => assetKeysOf(e).includes(previous)) &&
    !others.some(([, f]) => f.asset === previous)
  )
    ops.push({ op: "unpinAsset", key: previous });
  return ops;
}

export function setFallbackAlt(doc: CreativeDocument, alt: string): Ops {
  return doc.fallback ? [{ op: "setFallback", fallback: { ...clone(doc.fallback), alt } }] : [];
}

export function setMeta(doc: CreativeDocument, patch: Partial<CreativeDocument["meta"]>): Ops {
  return [{ op: "setMeta", meta: { ...clone(doc.meta), ...patch } }];
}

export function setBackgroundColor(doc: CreativeDocument, color: string, sceneId?: string): Ops {
  return [sceneSet(editingScene(doc, sceneId), { background: { kind: "solid", color } })];
}

/** Animation tracks of an element as resolved in a variant (the override list replaces the base list). */
export function resolvedTracks(doc: CreativeDocument, layoutId: string, elementId: string) {
  return overrideOf(doc, layoutId, elementId)?.animations ?? findElement(doc, elementId)?.animations ?? [];
}

/**
 * Copies of the interactions of copied elements (duplicate, paste, duplicate
 * scene): rules TRIGGERED by an element of the map are copied with every
 * element id of the map replaced; a CTA's own link is copied too.
 */
export function copyRules(
  doc: CreativeDocument,
  ids: ReadonlyMap<string, string>,
  scenes?: ReadonlyMap<string, string>
): Ops {
  const ops: Ops = [];
  const takenRules = new Set((doc.rules ?? []).map((r) => r.id));
  const remapAction = (a: Action): Action => {
    const out = clone(a) as Json;
    if (typeof out.target === "string" && ids.has(out.target)) out.target = ids.get(out.target);
    if (typeof out.scene === "string" && scenes?.has(out.scene)) out.scene = scenes.get(out.scene);
    if (out.type === "after") out.do = (a as Extract<Action, { type: "after" }>).do.map(remapAction);
    if (out.type === "link.open") {
      const owner = [...ids.keys()].find((k) => ownLinkId(k) === out.link);
      if (owner) out.link = ownLinkId(ids.get(owner) as string);
    }
    return out as unknown as Action;
  };
  for (const [from, to] of ids) {
    const original = doc.links.find((l) => l.id === ownLinkId(from));
    if (original && (doc.rules ?? []).some((r) => JSON.stringify(r).includes(`"${ownLinkId(from)}"`)))
      ops.push({ op: "setLink", link: { ...clone(original), id: ownLinkId(to) } });
  }
  for (const rule of doc.rules ?? []) {
    const on = clone(rule.on) as Json;
    const target = typeof on.target === "string" && ids.has(on.target) ? on.target : undefined;
    const scene = typeof on.scene === "string" && scenes?.has(on.scene) ? on.scene : undefined;
    if (!target && !scene) continue;
    if (target) on.target = ids.get(target);
    if (scene) on.scene = scenes?.get(scene);
    let id =
      target && rule.id === tapRuleId(target) ? tapRuleId(ids.get(target) as string) : nextId(takenRules, "rule");
    if (takenRules.has(id)) id = nextId(takenRules, "rule");
    takenRules.add(id);
    const copy: Rule = { ...clone(rule), id, on: on as unknown as Rule["on"], do: rule.do.map(remapAction) };
    ops.push({ op: "addRule", rule: copy });
  }
  return ops;
}

// ---------------------------------------------------------------- authoring sidecar (never compiled)

const toggle = (list: readonly string[] | undefined, id: string, on: boolean) => {
  const rest = (list ?? []).filter((x) => x !== id);
  return on ? [...rest, id] : rest;
};

export function setLocked(a: AuthoringState, id: string, locked: boolean): AuthoringState {
  return { ...a, locked: toggle(a.locked, id, locked) };
}

export function setEditorHidden(a: AuthoringState, id: string, hidden: boolean): AuthoringState {
  return { ...a, editorHidden: toggle(a.editorHidden, id, hidden) };
}

/** Drops sidecar entries of elements/variants that no longer exist (keeps the sidecar valid and small). */
export function pruneAuthoring(a: AuthoringState, doc: CreativeDocument): AuthoringState {
  const ids = new Set(doc.elements.map((e) => e.id));
  const layouts = new Set(doc.layouts.map((l) => l.id));
  const keep = (list?: readonly string[]) => (list ? list.filter((x) => ids.has(x)) : undefined);
  const lists = ["locked", "editorHidden", "collapsed"] as const;
  const out: Record<string, unknown> = Object.fromEntries(
    Object.entries(a).filter(([k]) => !(lists as readonly string[]).includes(k))
  );
  for (const key of lists) {
    const kept = keep(a[key]);
    if (kept !== undefined) out[key] = kept;
  }
  if (a.variants) out.variants = Object.fromEntries(Object.entries(a.variants).filter(([l]) => layouts.has(l)));
  return out as AuthoringState;
}
